/**
 * Action Input Component
 *
 * Text input for natural language action commands.
 * Matches user input to predefined actions from the library.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { CharacterRig, RigAction, ActionMatch } from '../../types/RiggingTypes';
import { matchActions } from '../../services/ActionLibrary';
import { ActionRegistry } from '../../services/ActionRegistry';
import { useCharacterImageUrl } from '../../services/characterStorageService';

interface ActionInputProps {
  character: CharacterRig;
  onApply: (character: CharacterRig, actionId: string) => void;
  onCancel: () => void;
}

const ActionInput: React.FC<ActionInputProps> = ({
  character,
  onApply,
  onCancel,
}) => {
  const [inputText, setInputText] = useState('');
  const [suggestions, setSuggestions] = useState<ActionMatch[]>([]);
  const [selectedAction, setSelectedAction] = useState<RigAction | null>(null);
  const [allActions] = useState<RigAction[]>(ActionRegistry.getAllActions());
  // The stored imageUrl may be a relative blob path; sign it for display.
  const imageSrc = useCharacterImageUrl(character.imageUrl);

  // Update suggestions when input changes
  useEffect(() => {
    if (inputText.trim()) {
      const matches = matchActions(inputText);
      setSuggestions(matches.slice(0, 5)); // Top 5 matches

      // Auto-select best match if confidence is high
      if (matches.length > 0 && matches[0].confidence > 0.5) {
        setSelectedAction(matches[0].action);
      } else {
        setSelectedAction(null);
      }
    } else {
      setSuggestions([]);
      setSelectedAction(null);
    }
  }, [inputText]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputText(e.target.value);
    },
    []
  );

  const handleActionSelect = useCallback((action: RigAction) => {
    setSelectedAction(action);
    setInputText(action.name);
  }, []);

  const handleApply = useCallback(() => {
    if (selectedAction) {
      onApply(character, selectedAction.id);
    }
  }, [selectedAction, character, onApply]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && selectedAction) {
        handleApply();
      }
    },
    [selectedAction, handleApply]
  );

  return (
    <div className="action-input-container">
      {/* Preview and Controls */}
      <div className="action-preview">
        {/* Character Preview */}
        <div className="action-character-preview">
          <img src={imageSrc} alt={character.imageName} />
        </div>

        {/* Action Controls */}
        <div className="action-controls">
          <h3 style={{ color: '#fff', margin: '0 0 12px 0' }}>
            Describe an action for "{character.imageName}"
          </h3>

          {/* Text Input */}
          <input
            type="text"
            className="action-text-input"
            placeholder="Type an action, e.g., 'waves hand happily' or 'jumps with joy'"
            value={inputText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            autoFocus
          />

          {/* Suggestions */}
          {suggestions.length > 0 && (
            <div className="action-suggestions">
              {suggestions.map((match) => (
                <div
                  key={match.action.id}
                  className={`action-suggestion ${selectedAction?.id === match.action.id ? 'selected' : ''}`}
                  onClick={() => handleActionSelect(match.action)}
                >
                  <div className="action-info">
                    <span className="action-name">{match.action.name}</span>
                    <span className="action-description">
                      {match.action.description}
                    </span>
                  </div>
                  <span className="action-confidence">
                    {Math.round(match.confidence * 100)}% match
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Action Library */}
      <div className="action-library-section">
        <h3>Or choose from action library:</h3>
        <div className="action-library-grid">
          {allActions.map((action) => (
            <div
              key={action.id}
              className={`action-library-item ${selectedAction?.id === action.id ? 'selected' : ''}`}
              onClick={() => handleActionSelect(action)}
              title={action.description}
            >
              <span>{action.name}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Selected Action Preview */}
      {selectedAction && (
        <div
          className="selected-action-preview"
          style={{
            marginTop: '16px',
            padding: '16px',
            background: 'rgba(54, 206, 220, 0.1)',
            borderRadius: '12px',
            border: '1px solid rgba(54, 206, 220, 0.3)',
          }}
        >
          <h4 style={{ color: '#36cedc', margin: '0 0 8px 0' }}>
            Selected: {selectedAction.name}
          </h4>
          <p
            style={{
              color: 'rgba(255,255,255,0.7)',
              margin: 0,
              fontSize: '0.9rem',
            }}
          >
            {selectedAction.description}
          </p>
          <p
            style={{
              color: 'rgba(255,255,255,0.5)',
              margin: '8px 0 0 0',
              fontSize: '0.8rem',
            }}
          >
            Duration: {selectedAction.duration}s • Animates{' '}
            {selectedAction.boneAnimations.length} bone(s)
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="action-buttons">
        <button className="action-btn cancel" onClick={onCancel}>
          Cancel
        </button>
        <button
          className="action-btn apply"
          onClick={handleApply}
          disabled={!selectedAction}
        >
          Apply Action
        </button>
      </div>
    </div>
  );
};

export default ActionInput;
